<template>
  <main class="home-page">

    <!-- ===== Hero ===== -->
    <section class="hero">
      <div class="hero__bg">
        <img
          class="hero__bg-img"
          src="/images/home/hero-bg.webp"
          alt="Briefing de l'équipe d'agents de sécurité Happy Protection"
          fetchpriority="high"
        />
        <div class="hero__gradient" aria-hidden="true"></div>
      </div>
      <div class="container hero__inner">
        <div class="hero__content">
          <div class="hero__badges">
            <span class="hero__cnaps-badge">Entreprise autorisée CNAPS</span>
            <div class="hero__divider" aria-hidden="true"></div>
            <span class="hero__tagline">Vigilance &amp; Expertise</span>
          </div>
          <h1 class="hero__title">Sécurité privée d'excellence à <span class="hero__title--accent">Nantes et en Loire-Atlantique</span></h1>
          <p class="hero__subtitle">
            Surveillance, Gardiennage et Sécurité événementielle de haute exigence.<br>
            <strong>Happy Protection Sécurité Surveillance</strong> protège vos actifs avec une rigueur absolue.
          </p>
          <div class="hero__actions">
            <RouterLink to="/devis" class="btn btn--primary">Demander un devis</RouterLink>
            <button type="button" class="btn btn--secondary" @click="showCallback = true">Être rappelé rapidement</button>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== Services ===== -->
    <section class="services-section">
      <div class="container">
        <div class="services-section__header">
          <div>
            <h2 class="services-section__title">Expertise Multi-Sectorielle</h2>
            <p class="services-section__subtitle">Des solutions de protection sur-mesure adaptées à la complexité de votre environnement opérationnel.</p>
          </div>
          <RouterLink to="/services" class="btn btn--accent-outline">Voir tous nos services</RouterLink>
        </div>
        <div class="services-grid">
          <article class="service-card">
            <span class="service-card__tag">Sur-mesure</span>
            <h3 class="service-card__title">Sécurité événementielle</h3>
            <p class="service-card__desc">Gestion de flux, contrôle d'accès et sécurisation de zones VIP pour vos grands rassemblements publics et privés.</p>
            <RouterLink to="/services" class="service-card__link">Découvrir le service</RouterLink>
          </article>
          <article class="service-card">
            <span class="service-card__tag">Protection</span>
            <h3 class="service-card__title">Gardiennage &amp; surveillance</h3>
            <p class="service-card__desc">Présence dissuasive fixe ou mobile et protection rigoureuse de vos actifs immobiliers et industriels 24h/24.</p>
            <RouterLink to="/services" class="service-card__link">Découvrir le service</RouterLink>
          </article>
          <article class="service-card">
            <span class="service-card__tag">Intervention</span>
            <h3 class="service-card__title">Rondes &amp; levée de doute</h3>
            <p class="service-card__desc">Interventions rapides motorisées sur déclenchement d'alarme pour une levée de doute efficace et sécurisée.</p>
            <RouterLink to="/services" class="service-card__link">Découvrir le service</RouterLink>
          </article>
          <article class="service-card">
            <span class="service-card__tag">Expertise</span>
            <h3 class="service-card__title">Sécurisation de sites sportifs</h3>
            <p class="service-card__desc">Accompagnement spécifique des clubs professionnels et gestion complexe des infrastructures sportives sensibles.</p>
            <RouterLink to="/services" class="service-card__link">Découvrir le service</RouterLink>
          </article>
        </div>
      </div>
    </section>

    <!-- ===== Why Us ===== -->
    <section class="why-section">
      <div class="container why-section__inner">
        <div class="why-section__left">
          <h2 class="why-section__title">
            Pourquoi choisir<br>
            <span class="why-section__title--accent">HAPPY PROTECTION ?</span>
          </h2>
          <p class="why-section__desc">Notre engagement repose sur une structure agile et une culture du résultat ancrée dans le territoire Nantais.</p>
          <div class="why-section__cnaps">
            <span class="why-section__cnaps-label">Certification Officielle</span>
            <span class="why-section__cnaps-num">Entreprise autorisée CNAPS n° <strong>AUT-044-2123-12-09-20240963313</strong></span>
          </div>
          <RouterLink to="/a-propos" class="btn btn--outline-light">En savoir plus sur nous</RouterLink>
        </div>
        <div class="why-grid">
          <div class="why-card">
            <span class="why-card__num">01</span>
            <h3 class="why-card__title">Réactivité 24h/24</h3>
            <p class="why-card__desc">Un centre de commandement prêt à déployer des agents en moins d'une heure sur Rezé et Nantes.</p>
          </div>
          <div class="why-card">
            <span class="why-card__num">02</span>
            <h3 class="why-card__title">Agents certifiés CNAPS</h3>
            <p class="why-card__desc">Tous nos agents détiennent la carte professionnelle CNAPS et suivent des formations continues rigoureuses.</p>
          </div>
          <div class="why-card">
            <span class="why-card__num">03</span>
            <h3 class="why-card__title">Encadrement expert</h3>
            <p class="why-card__desc">Une chaîne de commandement issue des métiers de la sécurité publique et privée avec une expertise terrain avérée.</p>
          </div>
          <div class="why-card">
            <span class="why-card__num">04</span>
            <h3 class="why-card__title">Devis rapide</h3>
            <p class="why-card__desc">Nous nous engageons à traiter vos demandes de chiffrage en un temps record pour une protection immédiate.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== Partners ===== -->
    <section class="partners-section">
      <div class="container">
        <p class="partners-section__label">Nos partenaires &amp; références majeures</p>
        <h3 class="partners-section__title">Ils nous font confiance</h3>
        <ul class="partners-grid" aria-label="Logos de nos partenaires">
          <li><img src="/images/home/partners/hbc-nantes.webp" alt="HBC Nantes" class="partner-logo" loading="lazy" /></li>
          <li><img src="/images/home/partners/hermine-nantes.webp" alt="Hermine Nantes" class="partner-logo" loading="lazy" /></li>
          <li><img src="/images/home/partners/neptunes-nantes.webp" alt="Neptunes Nantes" class="partner-logo" loading="lazy" /></li>
          <li><img src="/images/home/partners/stade-nantais.webp" alt="Stade Nantais" class="partner-logo" loading="lazy" /></li>
          <li><img src="/images/home/partners/ville-de-reze.webp" alt="Ville de Rezé" class="partner-logo" loading="lazy" /></li>
          <li><img src="/images/home/partners/le-set-bignon-nantes.webp" alt="Le Set Bignon Nantes" class="partner-logo" loading="lazy" /></li>
          <li><img src="/images/home/partners/nantes-metropole-futsal.webp" alt="Nantes Métropole Futsal" class="partner-logo" loading="lazy" /></li>
        </ul>
      </div>
    </section>

    <!-- ===== Final CTA ===== -->
    <section class="cta-section">
      <div class="container cta-section__inner">
        <h2 class="cta-section__title">Besoin d’un agent de sécurité à Nantes ou en Loire-Atlantique (44) ?</h2>
        <div class="cta-section__actions">
          <RouterLink to="/devis" class="btn btn--white">Demander un devis</RouterLink>
          <button type="button" class="btn btn--primary" @click="showCallback = true">Être rappelé rapidement</button>
        </div>
      </div>
    </section>

  </main>

  <CallbackModal v-model="showCallback" />
</template>

<script setup>
import { ref } from 'vue'
import { useHead } from '@unhead/vue'
import CallbackModal from '@/components/CallbackModal.vue'

const showCallback = ref(false)

useHead({
  title: 'Accueil | Happy Protection',
  meta: [
    { name: 'description', content: 'Happy Protection — Société de surveillance, gardiennage et sécurité privée à Nantes. Entreprise autorisée CNAPS.' },
    { property: 'og:title', content: 'Accueil | Happy Protection' },
    { property: 'og:description', content: 'Happy Protection — Société de surveillance, gardiennage et sécurité privée à Nantes.' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://happyprotection.fr/' },
    { property: 'og:image', content: 'https://happyprotection.fr/images/about/logo-hp.webp' },
  ],
  link: [{ rel: 'canonical', href: 'https://happyprotection.fr/' }],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Happy Protection',
        description: 'Société de sécurité privée à Rezé, Nantes. Surveillance, gardiennage et sécurité événementielle.',
        url: 'https://happyprotection.fr',
        telephone: '+33983415391',
        email: 'contact@happyprotection.fr',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '14 Rue de la Quératière',
          addressLocality: 'Rezé',
          postalCode: '44400',
          addressCountry: 'FR',
        },
        areaServed: ['Loire-Atlantique', 'Vendée', 'Maine-et-Loire'],
        openingHours: 'Mo-Su 00:00-23:59',
        logo: 'https://happyprotection.fr/images/about/logo-hp.webp',
      }),
    },
  ],
})
</script>

<style scoped>
/* ===== Hero ===== */
.hero {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background-color: var(--color-bg);
}

.hero__bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.hero__bg-img {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: auto;
  height: auto;
  min-width: 100%;
  min-height: 100%;
  object-fit: cover;
  object-position: center top;
  transform: scaleX(-1);
}

.hero__gradient {
  position: absolute;
  inset: 0;
  background: rgba(249,249,249,0.7);
}

@media (min-width: 768px) {
  .hero__gradient {
    background: linear-gradient(to right, var(--color-bg) 40%, rgba(249,249,249,0.9) 60%, rgba(249,249,249,0.4) 80%, transparent 100%);
    backdrop-filter: none;
  }
}

@media (min-width: 1024px) {
  .hero__gradient {
    background: linear-gradient(to right, var(--color-bg) 20%, rgba(249,249,249,0.75) 38%, rgba(249,249,249,0.15) 58%, transparent 80%);
  }
}

.hero__inner {
  position: relative;
  z-index: 1;
  padding-top: 5rem;
  padding-bottom: var(--sp-8);
}

.hero__content {
  max-width: 640px;
}

.hero__badges {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.hero__cnaps-badge {
  display: inline-block;
  background-color: #6bd1f8;
  color: #005970;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-badge);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  padding: 6px 16px;
  border-radius: 2px;
}

.hero__divider {
  width: 48px;
  height: 2px;
  background-color: var(--color-accent-light);
}

.hero__tagline {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.075em;
  color: var(--color-accent);
}

.hero__title {
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  text-transform: uppercase;
  letter-spacing: -0.05em;
  color: var(--color-heading);
  line-height: 1;
  margin-bottom: 1.25rem;
}

.hero__title--accent { color: var(--color-accent); }

.hero__subtitle {
  border-left: 4px solid var(--color-accent-light);
  padding-left: 1.75rem;
  padding-block: 0.75rem;
  font-family: var(--font-body);
  font-weight: 300;
  font-size: clamp(0.9375rem, 1.5vw, 1rem);
  line-height: 1.625;
  color: #3a3a3a;
  margin-bottom: 1.5rem;
  max-width: 576px;
}

.hero__subtitle strong {
  font-weight: 600;
  color: var(--color-heading);
  text-transform: uppercase;
  letter-spacing: -0.0375em;
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

/* ===== Buttons ===== */
.btn--secondary {
  background-color: #e2e2e2;
  color: var(--color-heading);
}
.btn--secondary:hover { opacity: 0.9; }

.btn--accent-outline {
  background-color: var(--color-accent);
  color: var(--color-white);
  font-size: var(--text-xs);
  padding: 1rem 2rem;
}
.btn--accent-outline:hover { opacity: 0.9; }

.btn--outline-light {
  border: 2px solid var(--color-accent-light);
  color: var(--color-accent-light);
  background: transparent;
  font-size: var(--text-xs);
  padding: 1rem 2rem;
}
.btn--outline-light:hover { background-color: rgba(107,209,248,0.1); }


/* ===== Services section ===== */
.services-section {
  background-color: var(--color-bg);
  padding-block: var(--sp-16);
}

.services-section__header {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  margin-bottom: var(--sp-8);
}

.services-section__title {
  font-size: clamp(2rem, 4vw, var(--text-h2-lg));
  letter-spacing: -0.05em;
  color: var(--color-heading);
  margin-bottom: 1rem;
}

.services-section__subtitle {
  font-family: var(--font-body);
  font-weight: 300;
  font-size: var(--text-base);
  color: var(--color-text);
  max-width: 576px;
}

.services-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

.service-card {
  background-color: var(--color-card-bg);
  border: 1px solid var(--color-card-border);
  padding: 2.0625rem;
  display: flex;
  flex-direction: column;
}

.service-card__tag {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-tag);
  text-transform: uppercase;
  letter-spacing: 0.0625em;
  color: #006782;
  margin-bottom: 1rem;
}

.service-card__title {
  font-size: var(--text-h3);
  text-transform: uppercase;
  color: #454545;
  margin-bottom: 1rem;
  line-height: 1.25;
}

.service-card__desc {
  font-family: var(--font-body);
  font-weight: 400;
  font-size: var(--text-sm);
  color: var(--color-text);
  line-height: 1.625;
  margin-bottom: 1rem;
}

.service-card__link {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-tag);
  text-transform: uppercase;
  letter-spacing: 0.125em;
  color: var(--color-accent);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.service-card__link::after { content: '→'; }
.service-card__link:hover { opacity: 0.8; }

/* ===== Why section ===== */
.why-section {
  background-color: var(--color-dark);
  padding-block: var(--sp-16);
}

.why-section__inner {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--sp-8);
}

.why-section__title {
  font-size: clamp(1.75rem, 4vw, var(--text-h2));
  text-transform: uppercase;
  letter-spacing: -0.05em;
  color: #f1f1f1;
  margin-bottom: var(--sp-3);
  line-height: 1;
}

.why-section__title--accent { color: var(--color-accent-light); }

.why-section__desc {
  font-family: var(--font-body);
  font-weight: 300;
  font-size: var(--text-base);
  line-height: 1.625;
  color: rgba(226,226,226,0.7);
  margin-bottom: var(--sp-3);
}

.why-section__cnaps {
  border-left: 4px solid var(--color-accent-light);
  padding-left: 1.75rem;
  padding-block: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: var(--sp-3);
}

.why-section__cnaps-label {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.075em;
  color: var(--color-accent-light);
}

.why-section__cnaps-num {
  font-family: var(--font-body);
  font-weight: 300;
  font-size: var(--text-sm);
  color: #f1f1f1;
}

.why-section__cnaps-num strong { font-weight: 700; }

.why-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1px;
  background-color: rgba(189,200,206,0.2);
}

.why-card {
  background-color: var(--color-dark);
  padding: 2.5rem;
}

.why-card__num {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 2.25rem;
  color: var(--color-accent-light);
  display: block;
  margin-bottom: 1.5rem;
  line-height: 1;
}

.why-card__title {
  font-size: var(--text-h4);
  text-transform: uppercase;
  color: #f1f1f1;
  margin-bottom: 0.75rem;
}

.why-card__desc {
  font-family: var(--font-body);
  font-weight: 400;
  font-size: var(--text-sm);
  color: rgba(226,226,226,0.6);
  line-height: 1.625;
}

/* ===== Partners ===== */
.partners-section {
  background-color: var(--color-partners-bg);
  padding-block: var(--sp-8);
  text-align: center;
}

.partners-section__label {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.225em;
  color: #4b5563;
  margin-bottom: 0.5rem;
}

.partners-section__title {
  font-size: var(--text-h4);
  text-transform: uppercase;
  color: var(--color-heading);
  margin-bottom: var(--sp-6);
}

.partners-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem 1.5rem;
  align-items: center;
  justify-items: center;
  list-style: none;
  padding: 0;
  margin: 0;
}

.partners-grid li {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.partner-logo {
  height: 110px;
  width: 100%;
  max-width: 220px;
  object-fit: contain;
}

/* ===== CTA section ===== */
.cta-section {
  position: relative;
  background-color: var(--color-accent);
  background-image: linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)), url('~@/assets/images/protection-cta.webp');
  background-size: cover;
  background-position: center 25%;
  padding-block: var(--sp-16);
  min-height: 480px;
  display: flex;
  align-items: center;
  overflow: hidden;
  text-align: center;
}

.cta-section__watermark {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: clamp(5rem, 20vw, 18.75rem);
  color: white;
  opacity: 0.08;
  letter-spacing: -0.05em;
  pointer-events: none;
  user-select: none;
  white-space: nowrap;
}

.cta-section__inner {
  position: relative;
  z-index: 1;
}

.cta-section__title {
  font-size: clamp(1.5rem, 4vw, var(--text-h2));
  text-transform: uppercase;
  letter-spacing: -0.05em;
  color: var(--color-white);
  margin-bottom: 2.5rem;
}

.cta-section__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
}

/* ===== Responsive ===== */
@media (min-width: 480px) {
  .partners-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 768px) {
  .services-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .services-section__header {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }

  .why-section__inner {
    grid-template-columns: 1fr 2fr;
    align-items: start;
  }

  .why-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (min-width: 1024px) {
  .services-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .partners-grid {
    grid-template-columns: repeat(7, 1fr);
  }
}
</style>
